// Firebase issues a valid session to an account whose verification link was never opened, so
// `emailVerified` is the only thing separating it from a confirmed one. Fails closed.
export const VERIFY_EMAIL_PATH = '/verify-email';

export const isEmailVerified = (user) => user?.emailVerified === true;

/** Firebase's provider id for an email/password credential. Anything else is federated. */
const PASSWORD_PROVIDER = 'password';

/**
 * Whether this session must complete email verification before it may go anywhere.
 *
 * <p>Only the email/password credential can. A Google or Microsoft sign-in had its address proved by
 * the identity provider, and there is no verification mail for the user to open — Firebase sets
 * `emailVerified` from what the provider asserts, and for Microsoft/OIDC it may never become true.
 * Gating on the flag alone would therefore send those accounts to /verify-email on every protected
 * route, forever, with nothing they could do about it.
 *
 * <p>`every`, not `some`: an account holding ANY federated provider is exempt. That is the half of
 * the rule that protects Microsoft. (Linking a federated provider makes Firebase set `emailVerified`
 * true anyway, so a mixed account does not reach here in practice.)
 *
 * <p>Still fails closed for the case that matters: a brand-new password signup has exactly one
 * provider, `password`, and an unset flag.
 *
 * <p>Note this is a routing decision, not a security control — the browser can be made to say
 * anything. The enforcement lives in the token path on the server (see MDCWebContextFilter); this
 * exists so a user meets a redirect rather than a wall of 403s.
 */
export const requiresVerification = (user) => {
    if (!user || isEmailVerified(user)) {
        return false;
    }
    const providers = user.providerData || [];
    // No provider list at all (a restored session, a mocked user) — treat as password and gate,
    // matching the previous behaviour rather than opening a hole on a missing field.
    if (providers.length === 0) {
        return true;
    }
    return providers.every((p) => p?.providerId === PASSWORD_PROVIDER);
};

